import type { KpiTarget } from '@zerohuman/shared'
import type { KpiBoard, KpiRag } from '@/api'

/** Company/team KPI target shape returned by the normalized KPI target API. */
export type KpiDirection = 'at_least' | 'at_most'
export type KpiBandType = 'absolute' | 'percentage'
export type KpiSourceKind = 'task' | 'person'

/**
 * What stands in for the target on a KPI that has none. "Yet" because a KPI with no target is one measuring a
 * baseline first (#1696): it takes readings and shows no light until its target is set from them.
 */
export const NO_TARGET_TEXT = 'No target yet'

export type Kpi = {
    id: string
    name: string
    unit: string
    targetValue: number | null
    targetDirection: KpiDirection | null
    amberBand: number | null
    amberBandType: KpiBandType | null
    onTargetWorseningRule: boolean
    cadenceMinutes: number | null
    sourceKind: KpiSourceKind | null
    sourceRef: string | null
    isKey: boolean
    /**
     * When a person signed it off (ISO 8601), or null while it waits for sign-off: until then no run sees it or records
     * a reading for it. Left out on a KPI the page has not saved yet.
     */
    signedOffAt?: string | null
}

/** Whether a stored KPI is waiting for a person to sign it off. One not yet saved, with no answer about it, is not. */
export function kpiPending(kpi: Pick<Kpi, 'signedOffAt'>): boolean {
    return kpi.signedOffAt === null
}

export function emptyKpi(id: string): Kpi {
    return {
        id,
        name: '',
        unit: '',
        targetValue: null,
        targetDirection: null,
        amberBand: null,
        amberBandType: null,
        onTargetWorseningRule: false,
        cadenceMinutes: null,
        sourceKind: null,
        sourceRef: null,
        isKey: false,
    }
}

/**
 * Which way is good, as every KPI form asks it. The stored value is unchanged: "higher is better" is a target
 * the reading should be at least, "lower is better" one it should be at most.
 */
export const KPI_DIRECTION_OPTIONS: ReadonlyArray<{ value: KpiDirection; label: string }> = [
    { value: 'at_least', label: 'Higher is better' },
    { value: 'at_most', label: 'Lower is better' },
]

/** Units the unit field suggests. The field stays free text: a unit is a label, and any other is accepted. */
export const KPI_UNIT_SUGGESTIONS: readonly string[] = [
    '%',
    'count',
    'minutes',
    'hours',
    'days',
    'per day',
    'per week',
    'per month',
]

export function kpiDirectionText(direction: KpiDirection | null): string {
    if (direction === 'at_least') return 'at least'
    return direction === 'at_most' ? 'at most' : ''
}

export function kpiTargetText(kpi: Kpi): string {
    if (kpi.targetValue === null) return NO_TARGET_TEXT
    return [kpiDirectionText(kpi.targetDirection), String(kpi.targetValue), kpi.unit]
        .filter(part => part !== '')
        .join(' ')
}

export function kpiBandText(kpi: Kpi): string {
    if (kpi.amberBand === null) return ''
    if (kpi.amberBandType === 'percentage') return `${kpi.amberBand}% of target`
    return kpi.unit ? `${kpi.amberBand} ${kpi.unit}` : String(kpi.amberBand)
}

export type KpiCadencePeriod = 'minutes' | 'hours' | 'days' | 'weeks'

/**
 * The periods a cadence is typed and read in, shortest first. The API holds a cadence as whole minutes: the period
 * is how the portal asks for it and says it, not something stored.
 */
export const KPI_CADENCE_PERIODS: ReadonlyArray<{
    value: KpiCadencePeriod
    label: string
    unit: string
    minutes: number
}> = [
    { value: 'minutes', label: 'Minutes', unit: 'minute(s)', minutes: 1 },
    { value: 'hours', label: 'Hours', unit: 'hour(s)', minutes: 60 },
    { value: 'days', label: 'Days', unit: 'day(s)', minutes: 1_440 },
    { value: 'weeks', label: 'Weeks', unit: 'week(s)', minutes: 10_080 },
]

/** The period a form starts on for a KPI with no cadence yet. */
const DEFAULT_CADENCE_PERIOD: KpiCadencePeriod = 'days'

export function kpiCadencePeriodMinutes(period: KpiCadencePeriod): number {
    return KPI_CADENCE_PERIODS.find(candidate => candidate.value === period)?.minutes ?? 1
}

/** The longest period a cadence is a whole number of: 10080 minutes reads in weeks, 120 in hours, 90 in minutes. */
export function kpiCadencePeriod(cadenceMinutes: number | null): KpiCadencePeriod {
    if (cadenceMinutes === null) return DEFAULT_CADENCE_PERIOD
    const whole = KPI_CADENCE_PERIODS.filter(period => cadenceMinutes % period.minutes === 0)
    return whole.at(-1)?.value ?? 'minutes'
}

export function kpiCadenceText(kpi: Kpi): string {
    if (kpi.cadenceMinutes === null) return ''
    const period = kpiCadencePeriod(kpi.cadenceMinutes)
    const unit = KPI_CADENCE_PERIODS.find(candidate => candidate.value === period)?.unit ?? 'minute(s)'
    return `every ${kpi.cadenceMinutes / kpiCadencePeriodMinutes(period)} ${unit}`
}

export function kpiSourceText(kpi: Kpi): string {
    return kpi.sourceKind && kpi.sourceRef ? `${kpi.sourceKind}: ${kpi.sourceRef}` : ''
}

/**
 * `kpi`'s target as the shared derivation reads it (`kpiLight`, `kpiOverdue`), or null for a KPI with no target,
 * which shows no light. A band set as a percentage of the target is the distance it comes to, as the API reads it.
 */
export function kpiTargetOf(kpi: Kpi): KpiTarget | null {
    if (kpi.targetValue === null || !kpi.targetDirection) return null
    const band = kpi.amberBand ?? 0
    return {
        value: kpi.targetValue,
        direction: kpi.targetDirection,
        amberBand: kpi.amberBandType === 'percentage' ? (Math.abs(kpi.targetValue) * band) / 100 : band,
        worsensOnTarget: kpi.onTargetWorseningRule,
    }
}

/**
 * How many of the Enterprise page's KPIs stand at each light: the company's own KPIs and each team's key KPI, the
 * lines its board draws. A line with no light (no key KPI set, or never read) is in none of the three.
 */
export function kpiBoardLights(board: KpiBoard): Record<KpiRag, number> {
    const lights: Record<KpiRag, number> = { green: 0, amber: 0, red: 0 }
    for (const rag of [...board.enterprise.map(kpi => kpi.rag), ...board.teams.map(team => team.key?.rag ?? null)]) {
        if (rag) {
            lights[rag] += 1
        }
    }
    return lights
}
